import React, { useMemo } from 'react';
import { HorizontalGrid } from './layout/HorizontalGrid';
import { xxLargeTextSize, semiBoldText, primaryText, baseTextSize, regularText, tertiaryText } from '../../styling/stylingHelper';
import { TimeControls } from './TimeControls';
import { TabbedSections, FilterOption } from './TabbedSections';
import { DropdownSelector } from './DropdownSelector';
import { cn } from '../../lib/utils';
import { TopSectionMobile } from './TopSectionMobile';
import { ArrowLeft } from 'lucide-react';
import { TimeViewOption } from './TimeControls';
import { FilterButton } from './FilterButton';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { PriceAlertChangeType } from '../../stores/appFiltersStore';

/**
 * Display label for an overall-type filter value. Dynamic values (revenue source types)
 * have no translation key, so they fall back to a capitalised version of the raw value.
 * Exported so anything that has to name the active filter (e.g. data exports) shows the
 * same label as the tab the user clicked.
 */
export const overallTypeLabel = (t: TFunction, option: string): string => {
  switch (option) {
    case 'all':
      return t('overallType.all');
    case 'food':
      return t('overallType.food');
    case 'drinks':
      return t('overallType.drinks');
    case 'non-food':
      return t('overallType.nonFood');
    case 'take-away':
      return t('overallType.takeAway');
    case 'other':
      return t('overallType.other');
    case 'dish':
      return 'Dishes';
    case 'ingredient':
      return 'Ingredients';
    default:
      return option.charAt(0).toUpperCase() + option.slice(1);
  }
};

export interface OrderFiltersProps {
  restaurantFilters?: string[];
  setRestaurantFilters?: (filters: string[]) => void;
  availableRestaurants?: { id: string; name: string }[];
  platformFilters?: string[];
  setPlatformFilters?: (filters: string[]) => void;
  availablePlatforms?: string[];
  sectionFilters?: string[];
  setSectionFilters?: (filters: string[]) => void;
  availableSections?: { id: string; name: string }[];
  categoryFilters?: string[];
  setCategoryFilters?: (filters: string[]) => void;
  availableCategories?: string[];
  subCategoryFilters?: string[];
  setSubCategoryFilters?: (filters: string[]) => void;
  availableSubCategories?: string[];
  onResetFilters?: () => void;
  activeFilterCount?: number;
}

export interface RevenueFiltersProps {
  restaurantFilters?: string[];
  setRestaurantFilters?: (filters: string[]) => void;
  availableRestaurants?: { id: string; name: string }[];
  revenueCategoryFilters?: string[];
  setRevenueCategoryFilters?: (filters: string[]) => void;
  availableRevenueCategories?: string[];
  /** Venue filter: distinct revenue source NAMES (e.g. "Untable", "bar POP"). */
  revenueSourceFilters?: string[];
  setRevenueSourceFilters?: (filters: string[]) => void;
  availableRevenueSources?: string[];
  menuCategoryFilters?: string[];
  setMenuCategoryFilters?: (filters: string[]) => void;
  availableMenuCategories?: { id: string; name: string }[];
  onResetFilters?: () => void;
  activeFilterCount?: number;
}

export interface PriceAlertFiltersProps {
  restaurantFilters?: string[];
  setRestaurantFilters?: (filters: string[]) => void;
  availableRestaurants?: { id: string; name: string }[];
  platformFilters?: string[];
  setPlatformFilters?: (filters: string[]) => void;
  availablePlatforms?: string[];
  sectionFilters?: string[];
  setSectionFilters?: (filters: string[]) => void;
  availableSections?: { id: string; name: string }[];
  categoryFilters?: string[];
  setCategoryFilters?: (filters: string[]) => void;
  availableCategories?: string[];
  changeType?: PriceAlertChangeType;
  setChangeType?: (type: PriceAlertChangeType) => void;
  onResetFilters?: () => void;
  activeFilterCount?: number;
}

export interface TopSectionFilters {
  orderFilters?: OrderFiltersProps;
  revenueFilters?: RevenueFiltersProps;
  priceAlertFilters?: PriceAlertFiltersProps;
}

export interface TopSectionProps {
  title: string;
  description?: string;
  backButton: boolean;
  handleGoBack?: () => void;
  dateRange: { start: Date, end: Date } | null;
  setDateRange: (range: { start: Date, end: Date }) => void;
  selectedTimeOption: TimeViewOption;
  setSelectedTimeOption: (v: TimeViewOption) => void;
  overallTypeOptions?: string[]; // List of options to show (e.g., ['all', 'food', 'drinks'])
  onOverallTypeChange?: (value: string) => void;
  currentOverallTypeValue?: string; // Current overall type filter from store
  onVatChange?: (value: string) => void;
  currentVatValue?: string;
  // Separate VAT toggles for dashboard (optional - when both provided, shows two separate toggles)
  onRevenueVatChange?: (value: string) => void;
  currentRevenueVatValue?: string;
  onOrdersVatChange?: (value: string) => void;
  currentOrdersVatValue?: string;
  // Filter props (optional - only passed when filters are needed)
  showFilters?: boolean;
  filters?: TopSectionFilters;
  // Custom control rendered in the top-right next to the time controls (e.g. an inline
  // restaurant-select dropdown). Shown in place of / alongside the filter button.
  rightControls?: React.ReactNode;
  // Keep the mobile (<lg) title visible. Default false: pages that render their own
  // MobilePageHeader already show the title there, so TopSectionMobile hides its
  // duplicate <h1> below lg. Set true on pages WITHOUT a MobilePageHeader.
  showMobileTitle?: boolean;
  // Mobile only: tuck rightControls + the separate VAT dropdowns behind a muted
  // "More options" toggle so the KPI cards are the first thing seen (dashboard).
  // Desktop (≥1100px) layout is unaffected.
  collapseSecondaryControlsOnMobile?: boolean;
}

// Note: These will be replaced with translated versions in the component
export const filterOptionsTimeKeys = [
  { value: 'all', labelKey: 'timeOptions.allTime' },
  { value: 'day', labelKey: 'timeOptions.day' },
  { value: 'week', labelKey: 'timeOptions.week' },
  { value: 'month', labelKey: 'timeOptions.month' },
  { value: 'quarter', labelKey: 'timeOptions.quarter' }
];

export const vatOptionsKeys = [
  { value: 'excl', labelKey: 'vatOptions.exclVat' },
  { value: 'incl', labelKey: 'vatOptions.inclVat' },
];

// Legacy exports for backwards compatibility - will be deprecated
export const filterOptionsTime: FilterOption[] = [
  { value: 'all', label: 'All time' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' }
];

export const vatOptions: FilterOption[] = [
  { value: 'excl', label: 'Excl. VAT' },
  { value: 'incl', label: 'Incl. VAT' },
];

export const TopSection: React.FC<TopSectionProps> = ({
  title,
  description,
  backButton,
  handleGoBack,
  dateRange,
  setDateRange,
  selectedTimeOption,
  setSelectedTimeOption,
  overallTypeOptions,
  onOverallTypeChange,
  currentOverallTypeValue = 'all',
  onVatChange,
  currentVatValue,
  onRevenueVatChange,
  currentRevenueVatValue,
  onOrdersVatChange,
  currentOrdersVatValue,
  showFilters = false,
  filters,
  rightControls,
  showMobileTitle = false,
  collapseSecondaryControlsOnMobile = false,
}) => {
  const { t } = useTranslation();

  // Create translated filter options
  const translatedFilterOptionsTime: FilterOption[] = useMemo(() => [
    { value: 'all', label: t('timeOptions.allTime') },
    { value: 'day', label: t('timeOptions.day') },
    { value: 'week', label: t('timeOptions.week') },
    { value: 'month', label: t('timeOptions.month') },
    { value: 'quarter', label: t('timeOptions.quarter') }
  ], [t]);

  const translatedVatOptions: FilterOption[] = useMemo(() => [
    { value: 'excl', label: t('vatOptions.exclVat') },
    { value: 'incl', label: t('vatOptions.inclVat') },
  ], [t]);

  // Check if we're using separate VAT toggles (dashboard mode)
  const hasSeparateVatToggles = !!(onRevenueVatChange && onOrdersVatChange);

  // Create labeled VAT options for separate toggles
  const revenueVatOptions: FilterOption[] = useMemo(() => [
    { value: 'excl', label: `${t('dashboard.revenueVatLabel')} ${t('vatOptions.exclVat')}` },
    { value: 'incl', label: `${t('dashboard.revenueVatLabel')} ${t('vatOptions.inclVat')}` },
  ], [t]);

  const ordersVatOptions: FilterOption[] = useMemo(() => [
    { value: 'excl', label: `${t('dashboard.ordersVatLabel')} ${t('vatOptions.exclVat')}` },
    { value: 'incl', label: `${t('dashboard.ordersVatLabel')} ${t('vatOptions.inclVat')}` },
  ], [t]);

  // Destructure orderFilters from filters object
  const orderFilters = filters?.orderFilters;
  const {
    restaurantFilters = [],
    setRestaurantFilters,
    availableRestaurants = [],
    platformFilters = [],
    setPlatformFilters,
    availablePlatforms = [],
    sectionFilters = [],
    setSectionFilters,
    availableSections = [],
    categoryFilters = [],
    setCategoryFilters,
    availableCategories = [],
    subCategoryFilters = [],
    setSubCategoryFilters,
    availableSubCategories = [],
    onResetFilters: orderResetFilters,
    activeFilterCount: orderActiveFilterCount = 0,
  } = orderFilters || {};

  // Destructure revenueFilters from filters object
  const revenueFilters = filters?.revenueFilters;
  const {
    restaurantFilters: revenueRestaurantFilters = [],
    setRestaurantFilters: setRevenueRestaurantFilters,
    availableRestaurants: availableRevenueRestaurants = [],
    revenueCategoryFilters = [],
    setRevenueCategoryFilters,
    availableRevenueCategories = [],
    revenueSourceFilters = [],
    setRevenueSourceFilters,
    availableRevenueSources = [],
    menuCategoryFilters = [],
    setMenuCategoryFilters,
    availableMenuCategories = [],
    onResetFilters: revenueResetFilters,
    activeFilterCount: revenueActiveFilterCount = 0,
  } = revenueFilters || {};

  // Destructure priceAlertFilters from filters object
  const priceAlertFilters = filters?.priceAlertFilters;
  const {
    restaurantFilters: priceAlertRestaurantFilters = [],
    setRestaurantFilters: setPriceAlertRestaurantFilters,
    availableRestaurants: availablePriceAlertRestaurants = [],
    platformFilters: priceAlertPlatformFilters = [],
    setPlatformFilters: setPriceAlertPlatformFilters,
    availablePlatforms: availablePriceAlertPlatforms = [],
    sectionFilters: priceAlertSectionFilters = [],
    setSectionFilters: setPriceAlertSectionFilters,
    availableSections: availablePriceAlertSections = [],
    categoryFilters: priceAlertCategoryFilters = [],
    setCategoryFilters: setPriceAlertCategoryFilters,
    availableCategories: availablePriceAlertCategories = [],
    changeType: priceAlertChangeType,
    setChangeType: setPriceAlertChangeType,
    onResetFilters: priceAlertResetFilters,
    activeFilterCount: priceAlertActiveFilterCount = 0,
  } = priceAlertFilters || {};

  // Determine which filter type we're using
  const hasOrderFilters = !!(setRestaurantFilters || setPlatformFilters || setSectionFilters || setCategoryFilters || setSubCategoryFilters);
  const hasRevenueFilters = !!(setRevenueRestaurantFilters || setRevenueCategoryFilters || setMenuCategoryFilters);
  const hasPriceAlertFilters = !!(setPriceAlertRestaurantFilters || setPriceAlertPlatformFilters || setPriceAlertSectionFilters || setPriceAlertCategoryFilters);

  // Use the appropriate reset function and count
  const onResetFilters = hasOrderFilters ? orderResetFilters : hasRevenueFilters ? revenueResetFilters : priceAlertResetFilters;
  const activeFilterCount = hasOrderFilters ? orderActiveFilterCount : hasRevenueFilters ? revenueActiveFilterCount : priceAlertActiveFilterCount;

  // Build overall type filter options with translations
  const overallTypeFilterOptions: FilterOption[] = useMemo(() => {
    if (!overallTypeOptions || overallTypeOptions.length === 0) {
      return [];
    }

    return overallTypeOptions.map(option => ({ value: option, label: overallTypeLabel(t, option) }));
  }, [overallTypeOptions, t]);

  const hasOverallTypeFilter = overallTypeFilterOptions.length > 0;

  const bottomMargin = description ? "mb-0" : "mb-6"

  return(
  <>
    {/* Desktop layout: visible above 1100px */}
    <div className="hidden min-[1100px]:block">
      {/* Top: Titles, filters remain in grid as before */}
      <div className={bottomMargin}>
        <HorizontalGrid className="items-start" flexValues={[1, 'auto']}>
          <div>
            <div className='flex'>
              {backButton && (
                <ArrowLeft className={cn("w-6 h-6 mt-1 mr-2 cursor-pointer", primaryText)} onClick={handleGoBack} />
              )}
              <h1 className={cn(xxLargeTextSize, semiBoldText, primaryText)}>
                {title}
              </h1>
            </div>
            {description && (
                <p className={cn(baseTextSize, regularText, tertiaryText, "mt-1")}>
                  {description}
                </p>
              )
            }
          </div>
          <div className="self-start">
            <div className='flex'>
              <TimeControls
                startingDate={dateRange?.start || new Date()}
                selectedTimeOption={selectedTimeOption}
                dateRange={dateRange}
                onChange={(startDate: Date, endDate: Date) =>
                  setDateRange({ start: startDate, end: endDate })
                }
              />
              {showFilters && onResetFilters && (hasOrderFilters || hasRevenueFilters || hasPriceAlertFilters) && (
                <div className="ml-2">
                  <FilterButton
                    restaurantFilters={restaurantFilters}
                    setRestaurantFilters={setRestaurantFilters}
                    availableRestaurants={availableRestaurants}
                    platformFilters={platformFilters}
                    setPlatformFilters={setPlatformFilters}
                    availablePlatforms={availablePlatforms}
                    sectionFilters={sectionFilters}
                    setSectionFilters={setSectionFilters}
                    availableSections={availableSections}
                    categoryFilters={categoryFilters}
                    setCategoryFilters={setCategoryFilters}
                    availableCategories={availableCategories}
                    subCategoryFilters={subCategoryFilters}
                    setSubCategoryFilters={setSubCategoryFilters}
                    availableSubCategories={availableSubCategories}
                    revenueCategoryFilters={revenueCategoryFilters}
                    setRevenueCategoryFilters={setRevenueCategoryFilters}
                    availableRevenueCategories={availableRevenueCategories}
                    revenueSourceFilters={revenueSourceFilters}
                    setRevenueSourceFilters={setRevenueSourceFilters}
                    availableRevenueSources={availableRevenueSources}
                    menuCategoryFilters={menuCategoryFilters}
                    setMenuCategoryFilters={setMenuCategoryFilters}
                    availableMenuCategories={availableMenuCategories}
                    revenueRestaurantFilters={revenueRestaurantFilters}
                    setRevenueRestaurantFilters={setRevenueRestaurantFilters}
                    availableRevenueRestaurants={availableRevenueRestaurants}
                    priceAlertRestaurantFilters={priceAlertRestaurantFilters}
                    setPriceAlertRestaurantFilters={setPriceAlertRestaurantFilters}
                    availablePriceAlertRestaurants={availablePriceAlertRestaurants}
                    priceAlertPlatformFilters={priceAlertPlatformFilters}
                    setPriceAlertPlatformFilters={setPriceAlertPlatformFilters}
                    availablePriceAlertPlatforms={availablePriceAlertPlatforms}
                    priceAlertSectionFilters={priceAlertSectionFilters}
                    setPriceAlertSectionFilters={setPriceAlertSectionFilters}
                    availablePriceAlertSections={availablePriceAlertSections}
                    priceAlertCategoryFilters={priceAlertCategoryFilters}
                    setPriceAlertCategoryFilters={setPriceAlertCategoryFilters}
                    availablePriceAlertCategories={availablePriceAlertCategories}
                    priceAlertChangeType={priceAlertChangeType}
                    setPriceAlertChangeType={setPriceAlertChangeType}
                    onVatChange={onVatChange}
                    currentVatValue={currentVatValue}
                    onResetFilters={onResetFilters}
                    activeFilterCount={activeFilterCount}
                    align="right"
                  />
                </div>
              )}
              {rightControls && (
                <div className="ml-2">{rightControls}</div>
              )}
            </div>
          </div>
        </HorizontalGrid>
      </div>
      <div className="mt-4 mb-4">
        <HorizontalGrid className="items-end" flexValues={[1, 'auto']}>
          {hasOverallTypeFilter && (
            <TabbedSections
              onboardingId="overall-type"
              options={overallTypeFilterOptions}
              value={currentOverallTypeValue}
              isTimeOptionSelection={false}
              onChange={onOverallTypeChange}
              maxTabsBeforeDropdown={5}
            />
          )}
          <div className="flex gap-2 justify-end items-end">
            <TabbedSections
              onboardingId="time-controls"
              options={translatedFilterOptionsTime}
              value={selectedTimeOption}
              onChange={(value: string) => setSelectedTimeOption(value as TimeViewOption)}
              isTimeOptionSelection={true}
            />
            {hasSeparateVatToggles ? (
              <div className="flex gap-2">
                <div className="min-w-[150px] max-[950px]:min-w-0">
                  <DropdownSelector
                    options={revenueVatOptions}
                    defaultValue={currentRevenueVatValue || revenueVatOptions[0].value}
                    onChange={onRevenueVatChange}
                  />
                </div>
                <div className="min-w-[150px] max-[950px]:min-w-0">
                  <DropdownSelector
                    options={ordersVatOptions}
                    defaultValue={currentOrdersVatValue || ordersVatOptions[0].value}
                    onChange={onOrdersVatChange}
                  />
                </div>
              </div>
            ) : onVatChange ? (
              <div>
                <DropdownSelector
                  options={translatedVatOptions}
                  defaultValue={currentVatValue || translatedVatOptions[0].value}
                  onChange={onVatChange}
                />
              </div>
            ) : null}
          </div>
        </HorizontalGrid>
      </div>
    </div>

    {/* Mobile layout: visible below 1100px */}
    <div className="block min-[1100px]:hidden">
      <TopSectionMobile
        title={title}
        backButton={backButton}
        handleGoBack={handleGoBack}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedTimeOption={selectedTimeOption}
        setSelectedTimeOption={setSelectedTimeOption}
        overallTypeFilterOptions={overallTypeFilterOptions}
        onOverallTypeChange={onOverallTypeChange}
        currentOverallTypeValue={currentOverallTypeValue}
        onVatChange={onVatChange}
        currentVatValue={currentVatValue}
        onRevenueVatChange={onRevenueVatChange}
        currentRevenueVatValue={currentRevenueVatValue}
        onOrdersVatChange={onOrdersVatChange}
        currentOrdersVatValue={currentOrdersVatValue}
        showFilters={showFilters}
        filters={filters}
        rightControls={rightControls}
        showMobileTitle={showMobileTitle}
        collapseSecondaryControlsOnMobile={collapseSecondaryControlsOnMobile}
      />
    </div>
  </>
)};
